import { Injectable } from '@angular/core';
import { Location } from '@angular/common';
import { Router } from '@angular/router';

/**
 * OrdersRouteService
 * Gestiona la navegación y detección de rutas para órdenes
 * Responsabilidad: Routing Logic
 */
@Injectable({
  providedIn: 'root'
})
export class OrdersRouteService {

  // Mapas de rutas consolidados
  private readonly ROUTES_MAP: { [key: string]: string } = {
    'orders/contracts/add': 'contracts',
    'orders/contracts': 'contracts',
    'orders/registering': 'registering',
    'orders/signed-contracts': 'signed-contracts',
    'orders/pre-delivery': 'pre-delivery',
    'orders/plates': 'plates',
    'orders/documentation': 'documentation',
    'orders/washing': 'washing',
    'orders/logistics': 'logistics',
    'orders/dealership-accessories': 'dealership-accessories',
    'orders/drafts': 'drafts',
    'orders/sqls': 'sqls',
    'orders/sql-drafts': 'sql_draft',
    'orders/frozen': 'frozen',
    'orders/offers': 'offers',
    'orders/delivered': 'delivered',
    'orders/cancelled': 'cancelled',
    'orders/denied': 'denied',
    'orders/digital': 'digital',
    'orders/rejected': 'rejected',
    'orders/authorized': 'authorized',
    'orders/cancellation-requested': 'cancellation-requested',
    'orders/pre-frozen': 'pre-frozen',
    'orders/ownership': 'ownership',
    'orders/dealership': 'dealership',
    'orders/transport': 'transport',
    'orders/supplier_rejected': 'supplier_rejected',
    'orders/vehicle_delivered': 'vehicle_delivered',
    'orders/new_vehicle_billing': 'new_vehicle_billing',
    'orders/new_vehicle_registered': 'new_vehicle_registered',
    'orders/after-sales-factory': 'after-sales-factory',
    'orders/after-sales-dealership': 'after-sales-dealership',
    'orders/after-sales-billing': 'after-sales-billing',
    'orders/after-sales-registering': 'after-sales-registering',
    'orders/after-sales-prepared': 'after-sales-prepared',
    'orders/after-sales-logistics': 'after-sales-logistics',
    'orders/after-sales-rejected': 'after-sales-rejected',
    'orders/after-sales-delivered': 'after-sales-delivered',
    'orders/not-delivered': 'not-delivered',
    'orders/after-sales-without-requirement': 'after-sales-without-requirement',
    'orders/all-contracts': 'all-contracts',
    'orders/all-after-sales': 'all-after-sales',
    'orders/telemarketing-pending': 'telemarketing-pending',
    'orders/telemarketing-done': 'telemarketing-done',
    'orders': ''
  };

  private readonly ROUTES_MAP_WORKSHOP: { [key: string]: string } = {
    'orders/registering': 'registering',
    'orders/pre-delivery': 'pre-delivery',
    'orders/plates': 'plates',
    'orders/documentation': 'documentation',
    'orders/washing': 'washing',
    'orders/logistics': 'logistics',
    'orders/dealership-accessories': 'dealership-accessories'
  };

  private readonly ROUTES_MAP_TELEMARKETING: { [key: string]: string } = {
    'orders/telemarketing-pending': 'telemarketing-pending',
    'orders/telemarketing-done': 'telemarketing-done'
  };

  constructor(
    private location: Location,
    private router: Router
  ) {
  }

  /**
   * Detecta el tipo de orden basado en la URL actual
   */
  detectOrderType(url: string): string {
    for (const route in this.ROUTES_MAP) {
      if (url.includes(route)) {
        return this.ROUTES_MAP[route];
      }
    }
    return '';
  }

  /**
   * Detecta el pre-tipo de orden (workshop, telemarketing, stages)
   */
  detectPreOrderType(url: string): string {
    // Verificar si es workshop
    for (const route in this.ROUTES_MAP_WORKSHOP) {
      if (url.includes(route)) {
        return 'delivery-status';
      }
    }

    // Verificar si es telemarketing
    for (const route in this.ROUTES_MAP_TELEMARKETING) {
      if (url.includes(route)) {
        return 'telemarketing-action-status';
      }
    }

    // Por defecto es stages
    return 'stages';
  }

  /**
   * Navega a una orden específica
   */
  navigateToOrder(reference: string, typeOrders: string): void {
    const route = typeOrders ? `/orders/${typeOrders}/${reference}` : `/orders/${reference}`;
    this.router.navigate([route]);
  }

  /**
   * Actualiza la URL sin recargar
   */
  updateURL(path: string): void {
    this.location.go(path);
  }

  /**
   * Obtiene el título de la ruta
   */
  getRouteTitle(orderType: string): string {
    return orderType === '' ? 'all' : orderType;
  }

  /**
   * Verifica si debe mostrar el filtro de estado
   */
  shouldShowStatusFilter(orderType: string): boolean {
    return orderType !== '' && orderType !== 'cancelled';
  }

  /**
   * Verifica si debe mostrar el filtro de cancelados
   */
  shouldShowCancelledFilter(orderType: string, showStatusFilter: boolean): boolean {
    return orderType !== 'cancelled' && !showStatusFilter;
  }

  /**
   * Detecta y configura el tipo de orden basado en la URL
   * Migrado desde orders.component.ts (línea ~650)
   */
  detectAndSetOrderType(url: string): {
    typeOrders: string;
    preTypeOrders: string;
    title: string;
    showAlertFilterAll: boolean;
    showAlertCancelled: boolean;
    stage_type: string;
    newRoute: string;
  } {
    let typeOrders = '';
    let preTypeOrders = 'stages';
    let title = 'all';
    let showAlertFilterAll = false;
    let showAlertCancelled = false;
    let stage_type = '';
    let newRoute = '/orders';

    // Detectar pre-tipo
    for (const route in this.ROUTES_MAP_WORKSHOP) {
      if (url.includes(route)) {
        preTypeOrders = 'delivery-status';
        break;
      }
    }

    for (const route in this.ROUTES_MAP_TELEMARKETING) {
      if (url.includes(route)) {
        preTypeOrders = 'telemarketing-action-status';
        break;
      }
    }

    // Detectar tipo de orden
    for (const route in this.ROUTES_MAP) {
      if (url.includes(route)) {
        typeOrders = this.ROUTES_MAP[route];
        stage_type = typeOrders;
        newRoute = `/orders/${typeOrders}`;
        title = typeOrders === '' ? 'all' : typeOrders;
        showAlertFilterAll = typeOrders !== '' && typeOrders !== 'cancelled';
        showAlertCancelled = typeOrders !== 'cancelled' && !showAlertFilterAll;
        break;
      }
    }

    return {
      typeOrders,
      preTypeOrders,
      title,
      showAlertFilterAll,
      showAlertCancelled,
      stage_type,
      newRoute
    };
  }

  /**
   * Detecta la ruta actual para lógica específica
   * Migrado desde detectCurrentRoute() en orders.component.ts
   */
  detectCurrentRoute(url: string): string {
    // Buscar en el routesMap para determinar el tipo de vista
    for (const [route, type] of Object.entries(this.ROUTES_MAP)) {
      if (url.includes(route)) {
        return type;
      }
    }

    // Alternativa: detectar por URL
    if (url.includes('after-sales') || url.includes('postventa')) {
      return 'after-sales';
    }

    return '';
  }

  /**
   * Verifica si es una vista de postventa
   */
  isAfterSalesView(url: string): boolean {
    const afterSalesRoutes = [
      'after-sales-factory',
      'after-sales-dealership',
      'after-sales-billing',
      'after-sales-registering',
      'after-sales-prepared',
      'after-sales-logistics',
      'after-sales-rejected',
      'after-sales-delivered',
      'after-sales-without-requirement',
      'all-after-sales'
    ];

    return afterSalesRoutes.some(route => url.includes(route));
  }

  /**
   * Obtiene todos los mapas de rutas
   */
  getRouteMaps(): {
    main: { [key: string]: string };
    workshop: { [key: string]: string };
    telemarketing: { [key: string]: string };
  } {
    return {
      main: this.ROUTES_MAP,
      workshop: this.ROUTES_MAP_WORKSHOP,
      telemarketing: this.ROUTES_MAP_TELEMARKETING
    };
  }

  /**
   * Construye la URL de orden
   */
  buildOrderURL(typeOrders: string, reference?: string): string {
    const base = `/orders/${typeOrders}`;
    return reference ? `${base}/${reference}` : base;
  }
}
